import { useCallback, useEffect, useMemo, useState } from 'react';
import { isSheinShopAuthorized } from '@/shein-platform/constants/sheinRoutes';
import {
  collectShopCategoryScope,
  filterShopsForOfficialTemplate,
  filterShopsForOfficialTemplates,
  mergeShopCategoryTrees,
} from '@/shein-platform/utils/sheinOfficialShopCategory';
import { sheinPlatformAPI } from '@/utils/api';

/**
 * 全部店铺视图：预加载各店类目作用域，供官方模板复制时按类目筛店。
 * @param {{ shops?: array, enabled?: boolean }} options
 */
export default function useSheinOfficialShopCategoryScopes({ shops = [], enabled = false } = {}) {
  const [scopeByShopId, setScopeByShopId] = useState(() => new Map());
  const [categoryTrees, setCategoryTrees] = useState([]);
  const [loading, setLoading] = useState(false);

  const authorizedShops = useMemo(
    () => (Array.isArray(shops) ? shops : []).filter((shop) => isSheinShopAuthorized(shop)),
    [shops],
  );

  useEffect(() => {
    if (!enabled || authorizedShops.length === 0) {
      setScopeByShopId(new Map());
      setCategoryTrees([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(authorizedShops.map(async (shop) => {
      const sid = String(shop.id);
      try {
        const res = await sheinPlatformAPI.getShopCategories(sid);
        const categories = res?.categories ?? res?.items ?? res?.list ?? res ?? [];
        return { shopId: sid, categories };
      } catch {
        return { shopId: sid, categories: [] };
      }
    }))
      .then((rows) => {
        if (cancelled) return;
        const nextScope = new Map();
        rows.forEach(({ shopId, categories }) => {
          nextScope.set(shopId, collectShopCategoryScope(categories));
        });
        setScopeByShopId(nextScope);
        setCategoryTrees(mergeShopCategoryTrees(rows.map((row) => row.categories)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, authorizedShops]);

  const shopsForOfficialTemplate = useCallback((template) => (
    filterShopsForOfficialTemplate(authorizedShops, scopeByShopId, template)
  ), [authorizedShops, scopeByShopId]);

  const shopsForOfficialTemplates = useCallback((templates) => (
    filterShopsForOfficialTemplates(authorizedShops, scopeByShopId, templates)
  ), [authorizedShops, scopeByShopId]);

  return {
    loading,
    scopeByShopId,
    mergedCategoryTree: categoryTrees,
    shopsForOfficialTemplate,
    shopsForOfficialTemplates,
  };
}
